import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { formatDistanceToNow, format } from 'date-fns'
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  ChartBarIcon,
  EyeIcon,
  CurrencyDollarIcon,
  CpuChipIcon,
  DocumentTextIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CircleStackIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  DocumentMagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import clsx from 'clsx'

// Types
interface AuditExecution {
  request_id: string
  agent_name: string
  execution_type: string
  started_at: string
  completed_at?: string
  duration_ms?: number
  success: boolean
  error_message?: string
  prompt?: string
  result_summary?: string
  total_tokens?: number
  total_cost_usd?: number
  llm_calls_count?: number
  quality_score?: number
  merkle_root?: string
  audit_event_count?: number
  project_id?: string
  user_id?: string
}

interface AuditEvent {
  event_id: string
  event_type: string
  timestamp: string
  details: Record<string, any>
  hash?: string
  decision_made?: string
  decision_reasoning?: string
  tokens_used?: number
  cost_usd?: number
}

interface AuditGroup {
  agent_name: string
  execution_type: string
  executions: AuditExecution[]
  total_executions: number
  total_tokens: number
  total_cost_usd: number
  success_rate: number
}

interface AuditStats {
  total_executions: number
  successful_executions: number
  failed_executions: number
  success_rate: number
  total_tokens: number
  total_cost_usd: number
  avg_duration_ms: number
  agents: Record<string, any>
}

interface ExecutionDetails {
  execution: AuditExecution
  events: AuditEvent[]
  decisions: Array<{
    decision_id: string
    decision_point: string
    decision_made: string
    alternatives_considered_json?: string
    reasoning?: string
    confidence?: number
    timestamp: string
  }>
  data_sources: Array<{
    type: string
    name: string
    query?: string
    records?: number
    timestamp: string
  }>
  llm_calls: Array<{
    call_id: string
    model: string
    provider: string
    input_tokens: number
    output_tokens: number
    cost_usd: number
    purpose: string
    timestamp: string
  }>
  cost_breakdown: Record<string, number>
}

const API_BASE = '/api/v1'

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('access_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

const AuditsPage = observer(() => {
  const [groups, setGroups] = useState<AuditGroup[]>([])
  const [stats, setStats] = useState<AuditStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [selectedExecution, setSelectedExecution] = useState<string | null>(null)
  const [groupBy, setGroupBy] = useState<'agent' | 'type' | 'project'>('agent')
  const [filterAgent, setFilterAgent] = useState<string>('all')
  const [filterSuccess, setFilterSuccess] = useState<'all' | 'success' | 'failed'>('all')
  const [agents, setAgents] = useState<string[]>([])
  const [dateRange, setDateRange] = useState<'all' | 'today' | '7d' | '30d' | 'custom'>('all')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')

  const getDateParams = (): string => {
    let params = ''
    const now = new Date()
    if (dateRange === 'today') {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      params = `&start_date=${start.toISOString()}`
    } else if (dateRange === '7d') {
      const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      params = `&start_date=${start.toISOString()}`
    } else if (dateRange === '30d') {
      const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      params = `&start_date=${start.toISOString()}`
    } else if (dateRange === 'custom') {
      if (startDate) params += `&start_date=${new Date(startDate).toISOString()}`
      if (endDate) {
        const end = new Date(endDate)
        end.setHours(23, 59, 59, 999)
        params += `&end_date=${end.toISOString()}`
      }
    }
    return params
  }

  useEffect(() => {
    fetchData()
    fetchAgents()
  }, [groupBy, dateRange, startDate, endDate])

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const dp = getDateParams()
      // Fetch grouped executions
      const groupsResponse = await fetch(
        `${API_BASE}/audit/executions/grouped?group_by=${groupBy}&limit_per_group=10${dp}`,
        { headers: authHeaders() }
      )
      if (groupsResponse.ok) {
        const data = await groupsResponse.json()
        setGroups(data.groups || [])
      }

      // Fetch stats
      const statsResponse = await fetch(`${API_BASE}/audit/stats?_=1${dp}`, { headers: authHeaders() })
      if (statsResponse.ok) {
        const data = await statsResponse.json()
        setStats(data)
      }
    } catch (error) {
      console.error('Error fetching audit data:', error)
      toast.error('Failed to load audit data')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${API_BASE}/audit/agents`, { headers: authHeaders() })
      if (response.ok) {
        const data = await response.json()
        setAgents(data || [])
      }
    } catch (error) {
      console.error('Error fetching agents:', error)
    }
  }

  const handleRefresh = () => {
    fetchData()
    toast.success('Refreshed')
  }

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups)
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey)
    } else {
      newExpanded.add(groupKey)
    }
    setExpandedGroups(newExpanded)
  }

  const filteredGroups = groups.filter((group) => {
    if (filterAgent !== 'all' && group.agent_name !== filterAgent) return false
    return true
  })

  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A'
    const seconds = Math.floor(ms / 1000)
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }

  const formatCost = (cost?: number) => {
    if (!cost) return '$0.00'
    if (cost < 0.01) return `$${cost.toFixed(6)}`
    return `$${cost.toFixed(4)}`
  }

  const getExecutionTypeIcon = (type: string) => {
    switch (type) {
      case 'agent':
        return <CpuChipIcon className="w-5 h-5" />
      case 'crew':
        return <DocumentTextIcon className="w-5 h-5" />
      case 'workflow':
        return <CircleStackIcon className="w-5 h-5" />
      default:
        return <CpuChipIcon className="w-5 h-5" />
    }
  }

  const getExecutionTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'agent':
        return 'bg-blue-500/20 text-blue-600'
      case 'crew':
        return 'bg-purple-500/20 text-purple-600'
      case 'workflow':
        return 'bg-green-500/20 text-green-600'
      default:
        return 'bg-muted text-foreground'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Audit & Telemetry
          </h1>
          <p className="text-muted-foreground mt-1">
            Track all agent executions, costs, decisions, and data access
          </p>
        </div>
        <Button variant="secondary" onClick={handleRefresh}>
          <ArrowPathIcon className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Executions</p>
                <p className="text-2xl font-bold text-foreground">
                  {stats.total_executions.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <DocumentMagnifyingGlassIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Success Rate</p>
                <p className="text-2xl font-bold text-green-600">
                  {(stats.success_rate * 100).toFixed(1)}%
                </p>
              </div>
              <div className="p-3 bg-green-500/20 rounded-lg">
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Tokens</p>
                <p className="text-2xl font-bold text-purple-600">
                  {stats.total_tokens.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <ChartBarIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Cost</p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatCost(stats.total_cost_usd)}
                </p>
              </div>
              <div className="p-3 bg-orange-500/20 rounded-lg">
                <CurrencyDollarIcon className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Duration</p>
                <p className="text-2xl font-bold text-cyan-600">
                  {formatDuration(stats.avg_duration_ms)}
                </p>
              </div>
              <div className="p-3 bg-cyan-500/20 rounded-lg">
                <ClockIcon className="w-6 h-6 text-cyan-600" />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex items-center gap-4">
            <div>
              <label className="text-sm text-muted-foreground mr-2">Group by:</label>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as 'agent' | 'type' | 'project')}
                className="px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="agent">Agent Name</option>
                <option value="type">Execution Type</option>
                <option value="project">Project</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-muted-foreground mr-2">Agent:</label>
              <select
                value={filterAgent}
                onChange={(e) => setFilterAgent(e.target.value)}
                className="px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Agents</option>
                {agents.map((agent) => (
                  <option key={agent} value={agent}>
                    {agent}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-muted-foreground mr-2">Status:</label>
              <select
                value={filterSuccess}
                onChange={(e) => setFilterSuccess(e.target.value as 'all' | 'success' | 'failed')}
                className="px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="success">Successful</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-muted-foreground mr-2">Period:</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as any)}
                className="px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {dateRange === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-muted-foreground text-sm">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}

            {(filterAgent !== 'all' || filterSuccess !== 'all' || dateRange !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilterAgent('all')
                  setFilterSuccess('all')
                  setDateRange('all')
                  setStartDate('')
                  setEndDate('')
                }}
              >
                <FunnelIcon className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Grouped Executions */}
      {isLoading ? (
        <Card className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading audit data...</p>
        </Card>
      ) : filteredGroups.length === 0 ? (
        <Card className="text-center py-12">
          <DocumentMagnifyingGlassIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No audit records found</h3>
          <p className="text-muted-foreground">
            Execute an agent to see audit records here
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredGroups.map((group) => {
            const groupKey = `${group.agent_name}-${group.execution_type}`
            const isExpanded = expandedGroups.has(groupKey)

            return (
              <Card key={groupKey} className="overflow-hidden">
                {/* Group Header */}
                <div
                  className="flex items-center justify-between cursor-pointer hover:bg-muted/50 p-4 -m-4"
                  onClick={() => toggleGroup(groupKey)}
                >
                  <div className="flex items-center gap-4">
                    {isExpanded ? (
                      <ChevronDownIcon className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronRightIcon className="w-5 h-5 text-muted-foreground" />
                    )}

                    <div className="flex items-center gap-2">
                      {getExecutionTypeIcon(group.execution_type)}
                      <span className="font-semibold text-foreground">
                        {group.agent_name}
                      </span>
                      <span
                        className={clsx(
                          'px-2 py-0.5 text-xs font-medium rounded-full',
                          getExecutionTypeBadgeColor(group.execution_type)
                        )}
                      >
                        {group.execution_type}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {group.total_executions}
                      </span>{' '}
                      executions
                    </div>
                    <div className="text-muted-foreground">
                      <span className="font-medium text-green-600">
                        {(group.success_rate * 100).toFixed(0)}%
                      </span>{' '}
                      success
                    </div>
                    <div className="text-muted-foreground">
                      <span className="font-medium text-purple-600">
                        {group.total_tokens.toLocaleString()}
                      </span>{' '}
                      tokens
                    </div>
                    <div className="text-muted-foreground">
                      <span className="font-medium text-orange-600">
                        {formatCost(group.total_cost_usd)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Expanded Executions */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="space-y-3">
                      {group.executions
                        .filter((exec) => {
                          if (filterSuccess === 'success' && !exec.success) return false
                          if (filterSuccess === 'failed' && exec.success) return false
                          return true
                        })
                        .map((execution) => (
                          <div
                            key={execution.request_id}
                            className={clsx(
                              'flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all',
                              execution.success
                                ? 'bg-green-500/10 border-green-500/20 hover:bg-green-500/20'
                                : 'bg-red-500/10 border-red-500/20 hover:bg-red-500/20'
                            )}
                            onClick={() => setSelectedExecution(execution.request_id)}
                          >
                            <div className="flex items-center gap-3">
                              {execution.success ? (
                                <CheckCircleIcon className="w-5 h-5 text-green-600" />
                              ) : (
                                <XCircleIcon className="w-5 h-5 text-red-600" />
                              )}

                              <div>
                                <div className="flex items-center gap-2">
                                  <code className="text-xs bg-card px-2 py-0.5 rounded border">
                                    {execution.request_id}
                                  </code>
                                  <span className="text-xs text-muted-foreground">
                                    {format(
                                      new Date(execution.started_at),
                                      'MMM d, yyyy HH:mm:ss'
                                    )}
                                  </span>
                                </div>
                                {execution.prompt && (
                                  <p className="text-sm text-foreground mt-1 truncate max-w-lg">
                                    {execution.prompt}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-4 text-sm">
                              {execution.duration_ms && (
                                <div className="text-muted-foreground">
                                  <ClockIcon className="w-4 h-4 inline mr-1" />
                                  {formatDuration(execution.duration_ms)}
                                </div>
                              )}
                              {execution.total_tokens && (
                                <div className="text-muted-foreground">
                                  {execution.total_tokens.toLocaleString()} tokens
                                </div>
                              )}
                              {execution.total_cost_usd && (
                                <div className="text-orange-600 font-medium">
                                  {formatCost(execution.total_cost_usd)}
                                </div>
                              )}
                              {execution.quality_score && (
                                <Badge
                                  variant={
                                    execution.quality_score >= 7 ? 'success' : 'warning'
                                  }
                                >
                                  {execution.quality_score.toFixed(1)}/10
                                </Badge>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSelectedExecution(execution.request_id)
                                }}
                              >
                                <EyeIcon className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}

      {/* Execution Detail Modal */}
      {selectedExecution && (
        <ExecutionDetailModal
          requestId={selectedExecution}
          onClose={() => setSelectedExecution(null)}
        />
      )}
    </div>
  )
})

// Execution Detail Modal Component
const ExecutionDetailModal = ({
  requestId,
  onClose,
}: {
  requestId: string
  onClose: () => void
}) => {
  const [details, setDetails] = useState<ExecutionDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<
    'overview' | 'events' | 'decisions' | 'data' | 'llm'
  >('overview')

  useEffect(() => {
    const loadDetails = async () => {
      setIsLoading(true)
      try {
        const response = await fetch(`${API_BASE}/audit/executions/${requestId}`, { headers: authHeaders() })
        if (response.ok) {
          const data = await response.json()
          setDetails(data)
        } else {
          toast.error('Failed to load execution details')
        }
      } catch (error) {
        console.error('Error loading execution details:', error)
        toast.error('Failed to load execution details')
      } finally {
        setIsLoading(false)
      }
    }
    loadDetails()
  }, [requestId])

  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A'
    const seconds = Math.floor(ms / 1000)
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }

  const formatCost = (cost?: number) => {
    if (!cost) return '$0.00'
    if (cost < 0.01) return `$${cost.toFixed(6)}`
    return `$${cost.toFixed(4)}`
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="w-full max-w-5xl max-h-[90vh] overflow-y-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-muted-foreground mt-4">Loading execution details...</p>
          </div>
        </Card>
      </div>
    )
  }

  if (!details) return null

  const { execution, events, decisions, data_sources, llm_calls, cost_breakdown } =
    details

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <Card className="w-full max-w-5xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-foreground">Execution Details</h2>
              <code className="text-sm text-muted-foreground">{execution.request_id}</code>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={execution.success ? 'success' : 'warning'}>
                {execution.success ? 'Success' : 'Failed'}
              </Badge>
              {execution.merkle_root && (
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <ShieldCheckIcon className="w-4 h-4" />
                  Verified
                </div>
              )}
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-muted-foreground transition-colors"
              >
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-border mb-6">
            <nav className="flex gap-4">
              {[
                { id: 'overview', label: 'Overview', icon: DocumentMagnifyingGlassIcon },
                { id: 'events', label: `Events (${events.length})`, icon: ClockIcon },
                {
                  id: 'decisions',
                  label: `Decisions (${decisions.length})`,
                  icon: LightBulbIcon,
                },
                {
                  id: 'data',
                  label: `Data Sources (${data_sources.length})`,
                  icon: CircleStackIcon,
                },
                { id: 'llm', label: `LLM Calls (${llm_calls.length})`, icon: CpuChipIcon },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={clsx(
                    'flex items-center gap-2 px-4 py-2 border-b-2 transition-colors',
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <>
                {/* Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                    <p className="text-xs text-blue-600 mb-1">Duration</p>
                    <p className="text-xl font-semibold text-blue-600">
                      {formatDuration(execution.duration_ms)}
                    </p>
                  </div>
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                    <p className="text-xs text-purple-600 mb-1">Total Tokens</p>
                    <p className="text-xl font-semibold text-purple-600">
                      {(execution.total_tokens || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
                    <p className="text-xs text-orange-600 mb-1">Total Cost</p>
                    <p className="text-xl font-semibold text-orange-600">
                      {formatCost(execution.total_cost_usd)}
                    </p>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                    <p className="text-xs text-green-600 mb-1">Quality Score</p>
                    <p className="text-xl font-semibold text-green-600">
                      {execution.quality_score?.toFixed(1) || 'N/A'}/10
                    </p>
                  </div>
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">Agent</h3>
                    <p className="text-foreground">{execution.agent_name}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">Type</h3>
                    <p className="text-foreground">{execution.execution_type}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">Started</h3>
                    <p className="text-foreground">
                      {format(new Date(execution.started_at), 'PPpp')}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">Completed</h3>
                    <p className="text-foreground">
                      {execution.completed_at
                        ? format(new Date(execution.completed_at), 'PPpp')
                        : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Prompt */}
                {execution.prompt && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">Prompt</h3>
                    <pre className="bg-muted/50 border border-border rounded-lg p-4 text-sm overflow-x-auto whitespace-pre-wrap">
                      {execution.prompt}
                    </pre>
                  </div>
                )}

                {/* Result Summary */}
                {execution.result_summary && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">
                      Result Summary
                    </h3>
                    <p className="text-foreground">{execution.result_summary}</p>
                  </div>
                )}

                {/* Error */}
                {execution.error_message && (
                  <div>
                    <h3 className="text-sm font-semibold text-red-600 mb-2">Error</h3>
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                      <p className="text-sm text-red-600">{execution.error_message}</p>
                    </div>
                  </div>
                )}

                {/* Cost Breakdown */}
                {Object.keys(cost_breakdown).length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">
                      Cost Breakdown by Model
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {Object.entries(cost_breakdown).map(([model, cost]) => (
                        <div
                          key={model}
                          className="bg-muted/50 border border-border rounded-lg p-3"
                        >
                          <p className="text-xs text-muted-foreground truncate">{model}</p>
                          <p className="text-sm font-semibold text-foreground">
                            {formatCost(cost)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'events' && (
              <div className="space-y-3">
                {events.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No audit events recorded</p>
                ) : (
                  events.map((event, index) => (
                    <div
                      key={event.event_id || index}
                      className="border border-border rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-muted px-2 py-1 rounded">
                            #{index + 1}
                          </span>
                          <Badge variant="gray">{event.event_type}</Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(event.timestamp), 'HH:mm:ss.SSS')}
                        </span>
                      </div>
                      {event.decision_made && (
                        <div className="mt-2">
                          <span className="text-xs text-muted-foreground">Decision: </span>
                          <span className="text-sm font-medium">{event.decision_made}</span>
                        </div>
                      )}
                      {event.tokens_used && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          Tokens: {event.tokens_used} | Cost: {formatCost(event.cost_usd)}
                        </div>
                      )}
                      {Object.keys(event.details).length > 0 && (
                        <pre className="mt-2 text-xs bg-muted/50 p-2 rounded overflow-x-auto">
                          {JSON.stringify(event.details, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'decisions' && (
              <div className="space-y-4">
                {decisions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No decisions recorded</p>
                ) : (
                  decisions.map((decision, index) => (
                    <div
                      key={decision.decision_id || index}
                      className="border border-border rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <LightBulbIcon className="w-5 h-5 text-yellow-500" />
                          <span className="font-medium">{decision.decision_point}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(decision.timestamp), 'HH:mm:ss')}
                        </span>
                      </div>
                      <div className="bg-green-500/10 border border-green-500/20 rounded p-3 mb-3">
                        <p className="text-sm font-medium text-green-600">
                          Decision: {decision.decision_made}
                        </p>
                        {decision.confidence && (
                          <p className="text-xs text-green-600 mt-1">
                            Confidence: {(decision.confidence * 100).toFixed(0)}%
                          </p>
                        )}
                      </div>
                      {decision.reasoning && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Reasoning:</p>
                          <p className="text-sm text-foreground">{decision.reasoning}</p>
                        </div>
                      )}
                      {decision.alternatives_considered_json && (
                        <div className="mt-2">
                          <p className="text-xs text-muted-foreground mb-1">Alternatives:</p>
                          <pre className="text-xs bg-muted/50 p-2 rounded">
                            {decision.alternatives_considered_json}
                          </pre>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'data' && (
              <div className="space-y-3">
                {data_sources.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No data sources accessed</p>
                ) : (
                  data_sources.map((source, index) => (
                    <div
                      key={index}
                      className="border border-border rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <CircleStackIcon className="w-5 h-5 text-blue-500" />
                          <span className="font-medium">{source.type}</span>
                          <Badge variant="gray">{source.name}</Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {source.records || 0} records
                        </span>
                      </div>
                      {source.query && (
                        <pre className="text-xs bg-muted/50 p-2 rounded overflow-x-auto">
                          {source.query}
                        </pre>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        {format(new Date(source.timestamp), 'HH:mm:ss.SSS')}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'llm' && (
              <div className="space-y-3">
                {llm_calls.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No LLM calls recorded</p>
                ) : (
                  llm_calls.map((call, index) => (
                    <div
                      key={call.call_id || index}
                      className="border border-border rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <CpuChipIcon className="w-5 h-5 text-purple-500" />
                          <span className="font-medium">{call.model}</span>
                          <Badge variant="gray">{call.provider}</Badge>
                          <Badge variant="primary">{call.purpose}</Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(call.timestamp), 'HH:mm:ss')}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-3">
                        <div className="bg-blue-500/10 rounded p-2">
                          <p className="text-xs text-blue-600">Input Tokens</p>
                          <p className="font-semibold text-blue-600">
                            {call.input_tokens.toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-green-500/10 rounded p-2">
                          <p className="text-xs text-green-600">Output Tokens</p>
                          <p className="font-semibold text-green-600">
                            {call.output_tokens.toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-orange-500/10 rounded p-2">
                          <p className="text-xs text-orange-600">Cost</p>
                          <p className="font-semibold text-orange-600">
                            {formatCost(call.cost_usd)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center mt-6 pt-6 border-t border-border">
            {execution.merkle_root && (
              <div className="text-xs text-muted-foreground">
                <span className="font-medium">Merkle Root:</span>{' '}
                <code className="bg-muted px-1 rounded">
                  {execution.merkle_root.slice(0, 16)}...
                </code>
              </div>
            )}
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default AuditsPage
